import { useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { getUserLocationFromIP } from "@/utils/geo-location";
import { getAuthRedirectUrl } from "@/utils/auth-config";
import { NavigationService } from "@/utils/navigation";

export const useAuthMethods = (setIsLoading: (loading: boolean) => void) => {
  const { toast } = useToast();

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Clean up any existing auth state first
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (cleanupError) {
        console.log("Auth cleanup (expected):", cleanupError);
      }
      
      // Ensure email is lowercase to prevent case sensitivity issues
      const normalizedEmail = email.toLowerCase().trim();
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: normalizedEmail, 
        password 
      });
      
      if (error) {
        console.error("Sign in error:", error);
        toast({
          title: "Authentication failed",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      if (data.user) {
        // Check if user is confirmed
        if (!data.user.email_confirmed_at) {
          toast({
            title: "Email not confirmed",
            description: "Please check your email and click the confirmation link to activate your account.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          return;
        }

        console.log("Sign in successful:", data.user.email);
        toast({
          title: "Welcome back!",
          description: "You have been signed in successfully.",
        });
        NavigationService.navigateTo("/dashboard");
      }
    } catch (error) {
      console.error("Error signing in:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, toast]);
  
  const signUp = useCallback(async (formData: any) => {
    try {
      setIsLoading(true);
      console.log("Starting signup process...", formData.email);
      
      const { email, password, fullName } = formData;
      
      // Normalize email to prevent case sensitivity issues
      const normalizedEmail = email.toLowerCase().trim();
      
      // Detect user location during signup
      let locationData = {};
      try {
        console.log("Detecting location during signup...");
        const detectedLocation = await getUserLocationFromIP();
        if (detectedLocation.country) {
          locationData = {
            country: detectedLocation.country,
            region: detectedLocation.region || null,
            city: detectedLocation.city || null,
          };
          console.log("Location detected during signup:", locationData);
        }
      } catch (locationError) {
        console.error("Error detecting location during signup:", locationError);
        // Continue with signup even if location detection fails
      }
      
      // Use the centralized redirect URL logic for email confirmation
      const redirectUrl = getAuthRedirectUrl('/auth');
      console.log("Using signup redirect URL:", redirectUrl);
      
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            fullName,
            ...locationData, // Add location data to user metadata
          },
          emailRedirectTo: redirectUrl,
        },
      });
      
      if (error) {
        console.error("Signup error:", error);
        toast({
          title: "Signup failed",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      if (data.user) {
        console.log("Signup successful:", {
          userId: data.user.id,
          email: data.user.email,
          emailConfirmed: !!data.user.email_confirmed_at,
          needsConfirmation: !data.user.email_confirmed_at
        });

        console.log("Signup successful - using Supabase native confirmation flow");
        
        // Let Supabase handle the confirmation email natively
        // Don't interfere with the native flow by sending custom emails or signing out
        
        // Return success but don't navigate to dashboard
        // The popup will be shown by the calling component
        return { 
          error: null, 
          needsConfirmation: !data.user.email_confirmed_at,
          user: data.user 
        };
      }
      
      return { error: null };
    } catch (error) {
      console.error("Error in signUp:", error);
      toast({
        title: "Signup failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return { error };
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, toast]);
  
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Clear any local storage auth data
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      await supabase.auth.signOut({ scope: 'global' });
      
      console.log("Sign out successful");
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
      
      NavigationService.navigateTo("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, toast]);

  return { signIn, signUp, signOut };
};
